import { Alert, Box, Chip } from "@mui/material";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { ESCURO, TEXTO_COLORIDO_ESCURO } from "assets/coresTema";
import { useMaterialUIController } from "context";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { errorAlert, infoAviso, successAlert } from "utils/Functions";
import { get, post } from "utils/WebService";

/** Texto do usuário dentro do html do SweetAlert. */
export const escaparHtml = (t) => String(t ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const VISIVEIS = 5;

const plural = (n, um, varios) => `${Number(n).toLocaleString("pt-BR")} ${n === 1 ? um : varios}`;

/** O que falta para o cliente: em quais carteiras as fichas estão sem campanha (ou sem fichas ainda). */
function motivo(cliente) {
    const carteiras = cliente.carteirasSemCampanha || [];
    if (!carteiras.length) return "Sem campanha (ainda sem fichas)";
    const porCarteira = carteiras.map(c => `${c.carteira}: ${plural(c.fichas, "ficha", "fichas")}`).join("; ");
    if (cliente.semNenhuma) return `Sem campanha · ${porCarteira}`;
    return `Sem campanha na carteira ${carteiras.map(c => c.carteira).join(", ")} · ${porCarteira}`;
}

/**
 * Aviso da tela de Clientes (pedido da Aline em 28/10/2026): os clientes sem campanha valendo, em que o cálculo e o acordo
 * da ficha não funcionam (GET /pessoa/sem-campanha: os liberados ao usuário, com as fichas das carteiras que ele vê).
 * "Criar campanha padrão" (Clientes > Editar) cria a mesma campanha que o cliente novo já ganha ao ser incluído.
 */
export default function ClientesSemCampanha({ atualizar, onAbrirCliente }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const [dados, setDados] = useState(null);
    const [verTodos, setVerTodos] = useState(false);
    const [criando, setCriando] = useState(null);

    const carregar = useCallback(() => {
        get("/pessoa/sem-campanha").then(setDados).catch(() => setDados(null));
    }, []);

    useEffect(() => {
        carregar();
    }, [carregar, atualizar]);

    const clientes = dados?.clientes || [];
    if (!clientes.length) return null;
    const lista = verTodos ? clientes : clientes.slice(0, VISIVEIS);
    const comFichas = clientes.filter(c => c.fichasSemCampanha > 0).length;

    const criar = (cliente) => {
        // A campanha nasce em cada carteira de cobrança do cadastro do cliente que ainda não tem campanha (Global = uma para todas)
        infoAviso(`Criar a campanha padrão de <b>${escaparHtml(cliente.nome)}</b> (${escaparHtml(cliente.campanhaPara || "para todas as carteiras")}; `
            + "nas carteiras que já têm campanha, não cria)?<br/><br/>Vale a partir de hoje: "
            + "à vista, sem desconto e sem multa, juros, honorários ou correção. Depois ajuste as regras em Ferramentas &gt; Campanhas.", () => {
            setCriando(cliente.id);
            post(`/pessoa/${cliente.id}/campanha-padrao`, {})
                .then(res => {
                    successAlert(`Campanha padrão criada: <b>${escaparHtml(res.campanha)}</b> (${escaparHtml(res.valores)}). `
                        + "Ajuste as regras em Ferramentas &gt; Campanhas.");
                    carregar();
                })
                .catch(err => errorAlert(escaparHtml(err.message || "Não foi possível criar a campanha.")))
                .finally(() => setCriando(null));
        });
    };

    return (
        <Alert severity="warning" sx={{
            mb: 2, fontSize: "0.85rem", "& .MuiAlert-message": { width: "100%" },
            // No escuro, o fundo claro do Alert do MUI apagava os botões (que usam os tons claros): bloco cinza, borda e ícone âmbar
            ...(darkMode && {
                backgroundColor: ESCURO.painel, color: ESCURO.texto, border: `1px solid ${TEXTO_COLORIDO_ESCURO.warning}`,
                "& .MuiAlert-icon": { color: TEXTO_COLORIDO_ESCURO.warning },
            }),
        }}>
            <MDTypography variant="button" fontWeight="bold" display="block" sx={{ color: "inherit" }}>
                {plural(clientes.length, "cliente sem campanha valendo", "clientes sem campanha valendo")}
                {comFichas > 0 ? ` (${plural(comFichas, "com fichas", "com fichas")})` : ""}
            </MDTypography>
            <MDTypography variant="caption" display="block" sx={{ color: "inherit", mb: 1 }}>
                Nas fichas deles o cálculo e o acordo não funcionam. Crie a campanha padrão e ajuste as regras em Ferramentas &gt; Campanhas.
                O cliente incluído agora já nasce com ela.
            </MDTypography>
            {lista.map(cliente => (
                <Box key={cliente.id} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, py: 0.5,
                    borderTop: "1px solid", borderColor: "divider" }}>
                    <Box sx={{ flex: "1 1 280px", minWidth: 0 }}>
                        <MDTypography variant="button" fontWeight="medium" sx={{ color: "inherit" }}>
                            {cliente.nome}
                        </MDTypography>
                        {!cliente.ativo && <Chip label="inativo" size="small" variant="outlined" sx={{ ml: 1, height: 18, fontSize: "0.65rem" }} />}
                        <MDTypography variant="caption" display="block" sx={{ color: "inherit" }}>
                            {motivo(cliente)}
                            {cliente.outrasCampanhas > 0
                                ? ` · tem ${plural(cliente.outrasCampanhas, "campanha", "campanhas")}, mas nenhuma valendo hoje para todas as fichas (inativa, fora da vigência ou só de marcação/subcarteira)`
                                : ""}
                            {` · Carteira do cliente: ${cliente.carteiraCliente || "Global (todas as carteiras)"}`}
                            {cliente.usaCampanha === false
                                ? " (não é de cobrança: mude a carteira do cliente ou cadastre a campanha em Ferramentas > Campanhas)"
                                : ""}
                        </MDTypography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        {dados.podeCriar && cliente.usaCampanha !== false && (
                            <MDButton size="small" variant="outlined" color="info" disabled={criando === cliente.id}
                                onClick={() => criar(cliente)}>
                                {criando === cliente.id ? "Criando..." : "Criar campanha padrão"}
                            </MDButton>
                        )}
                        <MDButton size="small" variant="text" color="info" onClick={() => onAbrirCliente(cliente.id)}>
                            Abrir cliente
                        </MDButton>
                    </Box>
                </Box>
            ))}
            {clientes.length > VISIVEIS && (
                <MDButton size="small" variant="text" color="info" sx={{ mt: 0.5, px: 0 }} onClick={() => setVerTodos(v => !v)}>
                    {verTodos ? "Mostrar menos" : `Ver todos (${clientes.length})`}
                </MDButton>
            )}
            {!dados.podeCriar && (
                <MDTypography variant="caption" display="block" sx={{ color: "inherit", mt: 0.5 }}>
                    Criar a campanha padrão por aqui exige a opção Editar em Clientes no seu perfil.
                </MDTypography>
            )}
        </Alert>
    );
}

ClientesSemCampanha.propTypes = {
    atualizar: PropTypes.number,
    onAbrirCliente: PropTypes.func.isRequired,
};
